import { computed, onMounted, onBeforeUnmount, ref, type ComputedRef, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import useErrorHandlerModule from '@/composable/useErrorHandlerModule'
import { useUserStore } from '@/modules/auth/store'
import { useServiceStore } from '@/modules/myServices/store'
import { websocketServiceStatusUpdatedConnect } from '@/services/websocketService'
import type { BfApplicationMenuNotification } from '@/typescript/types/ui-components/BfApplicationMenuI'

/**
 * The bell's contents, lifted out of `DropdownBell` so the design system's header can render them
 * through `BfApplicationMenu` instead of a bespoke panel.
 *
 * The five notices, their wording and their order are the ones the legacy dropdown shipped, and so
 * are the domain notices' routes; only the invoice notices' route differs (see `invoicePath`). The
 * store calls, the websocket subscription and the refresh on every resolved route are the same too —
 * the credit balance is charged online, so a stale balance is a wrong balance.
 */
export function useHeaderNotifications(): {
  notifications: ComputedRef<BfApplicationMenuNotification[]>
  isLoading: Ref<boolean>
} {
  const router = useRouter()
  const { errorHandler } = useErrorHandlerModule()
  const authStore = useUserStore()
  const serviceStore = useServiceStore()

  const isLoading = ref(false)

  const hasUser = (): boolean => Object.keys(authStore.getUser).length > 0

  const load = async (withUser: boolean): Promise<void> => {
    if (!hasUser()) return

    const tasks = withUser
      ? [serviceStore.getUserNotificationsData(), authStore.loadUser()]
      : [serviceStore.getUserNotificationsData()]

    isLoading.value = true
    try {
      const results = await Promise.allSettled(tasks)
      results.forEach(result => {
        if (result.status === 'rejected') errorHandler(result.reason as Error)
      })
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    await load(false)
    if (authStore.getUser.id) {
      try {
        await websocketServiceStatusUpdatedConnect(authStore.getUser.id)
      } catch (e) {
        errorHandler(e as Error)
      }
    }
  })

  /* PAYG services charge the credit balance online, so the figures are re-read on every navigation.
     The guard is removed on unmount — the legacy dropdown registered one per instance and never
     released it, which stacked duplicates if the header was ever re-created (sign out, sign in). */
  const stopGuard = router.beforeResolve(() => {
    void load(true)
  })

  onBeforeUnmount(() => {
    stopGuard()
  })

  /* Both invoice notices open the Unpaid tab of My invoices, whatever the count: the list is where
     the client picks one invoice to pay or starts a mass payment. */
  const invoicePath = '/my-invoices?status=Unpaid'

  const notifications = computed<BfApplicationMenuNotification[]>(() => {
    const out: BfApplicationMenuNotification[] = []
    const expirations = serviceStore.domainExpirations

    if (expirations?.hasDomainExpirationForWeek) {
      const week = expirations.domainExpirationForWeek
      out.push({
        text: `You have ${week.count} domain(s) expiring within the next 7 days.`,
        unread: true,
        action: { label: 'Renew Now', to: `/manage/domain/${week.id}/renew` },
      })
    }

    if (expirations?.hasDomainExpirationForMonth) {
      const month = expirations.domainExpirationForMonth
      out.push({
        text: `You have ${month.count} domain(s) expiring within the next 30 days.`,
        unread: true,
        action: { label: 'Renew Now', to: `/manage/domain/${month.id}/renew` },
      })
    }

    if (serviceStore.getUpcomingCount) {
      out.push({
        text: `You have ${serviceStore.getUpcomingCount} unpaid invoice(s). Pay them early for peace of mind.`,
        unread: true,
        action: { label: 'Pay now', to: invoicePath },
      })
    }

    if (serviceStore.getOverdueCount) {
      out.push({
        text:
          `You have ${serviceStore.getOverdueCount} overdue invoice(s) with a total balance due of ` +
          `${serviceStore.getOverdueAmount}. Pay them now to avoid any interruptions in service.`,
        unread: true,
        action: { label: 'Pay now', to: invoicePath },
      })
    }

    // A credit balance is good news, so it is never `unread` — the dot is for what needs acting on.
    const credit = authStore.getUser.credit
    if (Number(credit) > 0) {
      out.push({ text: `You have a credit balance of ${authStore.getUser.currency_code} ${credit}` })
    }

    return out
  })

  return { notifications, isLoading }
}

export default useHeaderNotifications
